import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { SelfCheckinHotelInfo } from '../hotel-info'
import { selfCheckinFetchGuestRoomAssign } from '@/apis/aipass'
import dayjs from 'dayjs'
import { CheckinReceipt } from './checkin-receipt'
import { SelfCheckinCompleteState } from '@/components/pages/self-checkin/complete'
import { isAllSameStayPeriod, StayPeriods } from '@/utils/stay-period'
import { RoomInfoType } from '@/models/self-checkin/card-reader'
import {
  completeWithPrinterBackgroundStyle,
  completeWithPrinterCardStyle,
  completeWithPrinterKeyLayoutStyle,
  completeWithPrinterRoomLayoutStyle,
} from './complete-with-printer-styles'
import { ButtonType } from '@/components/atoms/button'
import { CompletionButton } from '@/components/molecules/self-checkin/completion-button'

type Props = {
  hotelId: string
  checkinId: string
  basicInfo: {
    hotelNameLatin: string
    telephone: string
    frontOpenTime: string
    frontCloseTime: string
  }
  onClickTop: () => void
  onPrint: () => void
  state: SelfCheckinCompleteState
  completeReceiptHandled: boolean
}
export const IssueKeyWithPrinter: React.FC<Props> = ({ state, onClickTop, ...props }) => {
  const { t } = useTranslation()
  const [isCompletedPrint, setIsCompletedPrint] = useState(props.completeReceiptHandled || false)
  const [roomInfo, setRoomInfo] = useState<RoomInfoType[]>()

  const fetchGuestRoomAssignInfo = async () => {
    await selfCheckinFetchGuestRoomAssign({
      hotelId: props.hotelId,
      reservationIdWithAssignIds: state.selectedReservationAndRooms,
    })
      .then(res => {
        const assignRooms = props.checkinId ? Object.values(res).flat() : res
        assignRooms.sort((a, b) => {
          if (a.assignCheckinDate === b.assignCheckinDate) {
            return 0
          }
          return dayjs(a.assignCheckinDate).isBefore(dayjs(b.assignCheckinDate)) ? -1 : 1
        })
        setRoomInfo(assignRooms)
      })
      .catch(e => console.log(e))
  }

  useEffect(() => {
    fetchGuestRoomAssignInfo()
  }, [])

  // 部屋ごとの宿泊期間を取得
  const stayPeriods = useMemo(() => {
    if (!roomInfo) return [] as StayPeriods

    return roomInfo.map(item => ({
      roomCheckinDate: item.assignCheckinDate || '',
      roomCheckoutDate: item.assignCheckoutDate || '',
    })) as StayPeriods
  }, [roomInfo])

  // 宿泊期間が異なる場合、各部屋の宿泊日も部屋番号とともに表示する
  const roomNumbersWithStayPeriod = useMemo(() => {
    if (!roomInfo) return []

    return roomInfo.map(item => ({
      checkinDate: item.assignCheckinDate ? dayjs(item.assignCheckinDate).format('MM/DD') : '-',
      checkoutDate: item.assignCheckoutDate ? dayjs(item.assignCheckoutDate).format('MM/DD') : '-',
      roomNumber: item.roomNumber || '-',
    }))
  }, [roomInfo])

  return (
    <div css={completeWithPrinterBackgroundStyle}>
      <div
        css={[completeWithPrinterCardStyle, completeWithPrinterRoomLayoutStyle, completeWithPrinterKeyLayoutStyle]}
        className="key-print"
      >
        <div className="head-line-block">
          <img src={require('@/static/images/icon_checkin_orange.svg')}></img>
          <span>{t('Check-in completed')}</span>
        </div>
        <div className="announce-block">
          <div className="checkin-message-block">
            <div className="checkin-message">
              {t('SelfCheckIn.Printed accommodation guide.')}
              <br />
              <Trans i18nKey="SelfCheckIn.Please take it from your hand and enter the room.">
                Please take it from your hand and enter <span style={{ color: '#F2A40B' }}>the room.</span>
              </Trans>
            </div>
            <div className="room-block">
              <div className="sub-text">{t('Room number')}</div>
              <div className="number-text">
                {/* 客室の期間が全て同じ場合は、部屋番号を`、`で区切って表示 */}
                {isAllSameStayPeriod(stayPeriods) ? (
                  roomInfo?.map(item => item.roomNumber || '-').join('、')
                ) : (
                  // 客室の期間がひとつでも異なる場合は、部屋番号と宿泊期間を表示
                  <div className="room-date-list">
                    {roomNumbersWithStayPeriod.map((room, index) => (
                      <div key={index} className="room-date-item">
                        <span className="date-text">
                          {room.checkinDate} ~ {room.checkoutDate}
                        </span>
                        <span className="room-number-text">{room.roomNumber}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="sub-text">{t('SelfCheckIn.Key numbers are given in the receipt')}</div>
            </div>
            <CompletionButton
              buttonType={isCompletedPrint ? ButtonType.Primary : ButtonType.PrimaryDisabled}
              onClick={onClickTop}
              isDisabled={!isCompletedPrint}
            >
              {t('SelfCheckIn.Confirmed')}
            </CompletionButton>
          </div>
          <div className="printer-demo-block">
            <img src={require('@/static/images/self-checkin/checkin-printer-a.png')} />
            <img src={require('@/static/images/self-checkin/checkin-printer-b.png')} />
          </div>
        </div>
        {!isCompletedPrint && (
          <CheckinReceipt
            state={state}
            onComplete={() => {
              setIsCompletedPrint(true)
              props.onPrint()
            }}
            isIssueRoomKey={true}
          />
        )}
      </div>
      <SelfCheckinHotelInfo
        telephone={props.basicInfo.telephone}
        frontOpenTime={props.basicInfo.frontOpenTime}
        frontCloseTime={props.basicInfo.frontCloseTime}
        position={'static'}
        usedIn="roomGuidance"
      />
    </div>
  )
}
